import { Box } from "@chakra-ui/react";
import { Reorder } from "framer-motion";
import { FormattedMessage, useIntl } from "react-intl";

import { RanaButton, RanaModal } from "@nens/ranalib";

import { PublicationNoItemText } from "../../components/no-item-text";
import { usePublicationStore } from "../../store/store";
import { Layer } from "../../types/publication";

import { FolderItem } from "./components/layer-list-groupitem";
import { LayerItem } from "./components/layer-list-item";
import { useLayerList } from "./hooks/use-layer-list";
import { buildFlatOrder, getFolderEndMarkerId } from "./utils/reorder-utils";

export function PublicationLeftLayerList() {
  const {
    layers,
    effectiveLayers,
    rows,
    removeModalFor,
    openRemoveLayerModal,
    closeRemoveLayerModal,
    handleLayerClick,
    containerRef,
    handleDragStart,
    handleDrag,
    handleDragEnd,
    handleReorder,
  } = useLayerList();

  return (
    <Box borderBottomWidth="base" borderColor="brand.glacier" pb={3}>
      {removeModalFor && <RemoveLayerDialog layer={removeModalFor} close={closeRemoveLayerModal} />}
      <PublicationNoItemText show={!layers.length}>
        <FormattedMessage
          id="publication.map.layerlist.no_layers"
          defaultMessage="No layers added to this map yet"
        />
      </PublicationNoItemText>
      <Reorder.Group
        as="div"
        ref={containerRef}
        axis="y"
        values={buildFlatOrder(effectiveLayers)}
        onReorder={handleReorder}
      >
        {rows.map((row) => {
          if (row.kind === "layer") {
            const siblings = row.folder ? row.folder.layers : layers;
            const siblingLayerIds = siblings
              .filter((item): item is Layer => item.type === "layer")
              .map((item) => item.id);
            const position = siblingLayerIds.indexOf(row.layer.id);

            return (
              <LayerItem
                key={row.layer.id}
                layer={row.layer}
                folder={row.folder}
                indent={row.folder ? 1 : 0}
                isFirst={position === 0}
                isLast={position === siblingLayerIds.length - 1}
                onDragStart={() => handleDragStart(row.layer.id)}
                removeLayer={openRemoveLayerModal}
                onLayerClick={handleLayerClick}
                onDrag={handleDrag}
                onDragEnd={handleDragEnd}
              />
            );
          }

          if (row.kind === "folder-header") {
            const position = layers.findIndex((item) => item.id === row.folder.id);
            return (
              <FolderItem
                key={row.folder.id}
                layer={row.folder}
                isFirst={position === 0}
                isLast={position === layers.length - 1}
                onDragStart={() => handleDragStart(row.folder.id)}
                onDrag={handleDrag}
                onDragEnd={handleDragEnd}
              />
            );
          }

          if (row.kind === "folder-empty") {
            return (
              <PublicationNoItemText key={`${row.folder.id}-empty`}>
                <FormattedMessage
                  id="publication.map.layerlist.no_layers_in_folder"
                  defaultMessage="No layers in this folder yet"
                />
              </PublicationNoItemText>
            );
          }

          // Invisible, non-draggable marker that registers a position in the single root
          // Reorder.Group right after this folder's children, so the flat drag-reordered id list
          // can tell where this folder's children end (see `reorder-utils.ts`).
          return (
            <Box key={`${row.folder.id}-end`} mb={1}>
              <Reorder.Item
                as="div"
                value={getFolderEndMarkerId(row.folder.id)}
                data-row-id={getFolderEndMarkerId(row.folder.id)}
                drag={false}
                style={{ height: 0, margin: 0, padding: 0, pointerEvents: "none" }}
              />
            </Box>
          );
        })}
      </Reorder.Group>
    </Box>
  );
}

function RemoveLayerDialog({ layer, close }: { layer: Layer; close: () => void }) {
  const intl = useIntl();
  const removeLayer = usePublicationStore((state) => state.removeLayer);

  return (
    <RanaModal
      isOpen
      onClose={close}
      topBar={intl.formatMessage({
        id: "publication.map.removelayermodal.title",
        defaultMessage: "Remove layer",
      })}
      buttons={[
        <RanaButton key="cancel" variant="secondary" onClick={close}>
          <FormattedMessage id="publication.layer.button.cancel" defaultMessage="Cancel" />
        </RanaButton>,
        <RanaButton
          key="remove"
          variant="danger"
          onClick={() => {
            removeLayer(layer.id);
            close();
          }}
        >
          <FormattedMessage
            id="publication.layer.button.remove_layer"
            defaultMessage="Remove layer"
          />
        </RanaButton>,
      ]}
    >
      <Box p={1}>
        <FormattedMessage
          id="publication.layer.removelayermodal.message"
          defaultMessage='Are you sure you want to remove the layer "{name}"?'
          values={{ name: layer.name }}
        />
      </Box>
    </RanaModal>
  );
}
